import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderCommunications: build.query<
      GetV2ProviderCommunicationsApiResponse,
      GetV2ProviderCommunicationsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/communications`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          patient_id: queryArg.patientId,
          organization_id: queryArg.organizationId,
          communication_type: queryArg.communicationType,
          channel: queryArg.channel,
          status: queryArg.status,
          start_date: queryArg.startDate,
          end_date: queryArg.endDate,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderCommunicationsApiResponse =
  /** status 200 OK */ CommunicationListResponse;
export type GetV2ProviderCommunicationsApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Filter by patient ID */
  patientId?: string | null;
  /** Filter by organization ID */
  organizationId?: string | null;
  /** Filter by communication type. Comma-separated for multiple (e.g., 'form,document,lab') */
  communicationType?: string | null;
  /** Filter by channel. Comma-separated for multiple (e.g., 'sms,email') */
  channel?: string | null;
  /** Filter by delivery status. Comma-separated for multiple (e.g., 'delivered,sent') */
  status?: string | null;
  /** Start date for filtering (ISO 8601 format) */
  startDate?: string | null;
  /** End date for filtering (ISO 8601 format) */
  endDate?: string | null;
  /** Field to sort by (created_at, sent_at, delivered_at) */
  sortBy?: string;
  /** Sort order (asc or desc) */
  sortOrder?: string;
};
export type ChannelDeliveryInfo = {
  /** Channel: sms or email */
  channel: string;
  /** When message was delivered */
  delivered_at?: string | null;
  /** Error details if delivery failed */
  error_message?: string | null;
  /** When delivery failed */
  failed_at?: string | null;
  /** When message was sent */
  sent_at?: string | null;
  /** Delivery status: queued, sent, delivered, failed, bounced, dropped, opened, clicked */
  status: string;
  /** Unique identifier for the touchpoint */
  touchpoint_id: string;
};
export type FormStatusInfo = {
  /** ID of the form session */
  form_session_id: string;
  /** Form status: pending, in_progress, or completed */
  status: string;
  /** When the form was submitted */
  submitted_at?: string | null;
};
export type InsuranceSummary = {
  /** Coverage priority: 1=primary, 2=secondary, 3=tertiary */
  coverage_order?: number;
  /** Group/employer number */
  group_number?: string | null;
  /** Member/policy ID on insurance card */
  member_id?: string | null;
  /** Payer logo image URL */
  payer_logo_url?: string | null;
  /** Insurance payer display name */
  payer_name?: string | null;
  /** Plan name (e.g. Gold PPO) */
  plan_name?: string | null;
  /** Coverage status: active, pending, cancelled, expired */
  status?: string;
};
export type PatientInfo = {
  dob?: string | null;
  first_name?: string | null;
  identifier?: string | null;
  /** Active insurance coverages with payer info */
  insurance_coverages?: InsuranceSummary[] | null;
  last_name?: string | null;
  middle_name?: string | null;
  name?: string | null;
  patient_id: string;
  preferred_name?: string | null;
  telephone?: string | null;
};
export type CommunicationItem = {
  /** Per-channel delivery details */
  channels: ChannelDeliveryInfo[];
  /** Type: form, document, appointment, lab */
  communication_type: string;
  /** When the communication was created (ISO 8601 UTC) */
  created_at: string;
  /** Dispatch ID grouping multi-channel sends */
  dispatch_id?: string | null;
  /** Document ID if a PDF exists for this communication */
  document_id?: string | null;
  /** Form lifecycle status if communication is form-related */
  form_status?: FormStatusInfo | null;
  /** Human-readable name (e.g., form title, document name, 'Lab Requisition Order') */
  name?: string | null;
  /** Patient information if available */
  patient?: PatientInfo | null;
  /** Patient this communication was sent to */
  patient_id: string;
  /** Source: manual, automated, or scheduled */
  source: string;
};
export type CommunicationListResponse = {
  items: CommunicationItem[];
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
